'use-client';
import React, {
  useMemo,
  useEffect,
  useRef,
  useImperativeHandle,
  useState,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import semanticClassNames from '../FastGallery.semanticClassNames';
import type { IFastGalleryCompProps } from '../FastGallery.types';
import styles from './style/FastGallery.scss';
import { CLICK_ACTION } from '../FastGallery.types';
import { Gallery } from '@wix/fast-gallery-components';
import type {
  EventData,
  EventsListenerFunction,
  CustomGalleryRenderers,
} from '@wix/fast-gallery-library';
import { GALLERY_EVENTS } from '@wix/fast-gallery-library';
import MeshContainer from '@wix/thunderbolt-elements/components/MeshContainer';
import ResponsiveContainer from '@wix/thunderbolt-elements/components/ResponsiveContainer';
import {
  convertWixSDKItemsToContentItems,
  convertOrganizeMediaPanelItemsToContentItems,
} from './FastGallery.galleryUtils';
import { getResizeMediaUrl } from '@wix/photography-client-lib';
import { useMasterPage } from '../../../common/hooks/useMasterPage';
import { FastGalleryImageRenderer } from './FastGalleryImageRenderer';

const FastGallery: React.FC<IFastGalleryCompProps> = forwardRef(
  (props, ref) => {
    const {
      id,
      customClassNames = [],
      wixSDKItems,
      items = [],
      onItemClicked = () => {},
      onGetMoreItems = () => {},
      children,
      containerProps,
      containerRootClassName,
      hasResponsiveLayout,
      meshProps,
      itemClickAction,
      partToPreviewStateMap,
      compPreviewState,
      setIsExpanded,
      isExpanded = false,
      shouldUseCustomImageRenderer,
    } = props;
    useEffect(() => {
      if (compPreviewState === 'SHOW_OVERLAY') {
        setIsExpanded(true);
      } else if (compPreviewState === 'HIDE_OVERLAY') {
        setIsExpanded(false);
      }
    }, [compPreviewState, setIsExpanded]);
    const masterPage = useMasterPage();

    const resizeMediaUrlArgs = {
      imageToken: '',
      staticMediaUrls: {},
      isPrerenderMode: false,
      useOptimizedImageFormat: true,
    };
    const getResizedMediaSrc = getResizeMediaUrl(resizeMediaUrlArgs);
    const isSsr = typeof window === `undefined`;
    const windowRef = useRef<Window>(!isSsr ? window : null);
    const wrapperRef = !isSsr ? windowRef : undefined;

    const galleryItems = useMemo(
      () =>
        wixSDKItems?.length > 0
          ? convertWixSDKItemsToContentItems(wixSDKItems)
          : convertOrganizeMediaPanelItemsToContentItems(items),
      [wixSDKItems, items],
    );
    const renderOverlay = (_children: React.ReactNode) =>
      itemClickAction === CLICK_ACTION.ACTION && (
        <div
          className={classNames(styles.expandRoot, {
            [styles.showExpand]: isExpanded,
          })}
        >
          {_children}
        </div>
      );
    const renderChildren = (_children: React.ReactNode) => {
      if (!_children) {
        return null;
      }
      const childrenTorender = renderOverlay(_children);
      return masterPage
        ? createPortal(childrenTorender, masterPage)
        : childrenTorender;
    };

    const [navigationApi, setNavigationApi] = useState<any>(undefined);

    const eventsListener: EventsListenerFunction = (
      eventName: GALLERY_EVENTS,
      eventData: EventData,
    ) => {
      switch (eventName) {
        case GALLERY_EVENTS.ON_ITEM_CLICKED:
          onItemClicked({
            itemIndex: eventData.index ?? -1,
            type: eventData.type,
          });
          break;
        case GALLERY_EVENTS.NEED_MORE_ITEMS:
          onGetMoreItems({
            from: eventData.index ?? -1,
          });
          break;
        case GALLERY_EVENTS.NAVIGATION_API_READY:
          setNavigationApi(eventData);
          break;
        // TODO: add GALLERY_NAVIGATION_START event to fast-gallery-library
        // case GALLERY_EVENTS.GALLERY_NAVIGATION_START:
        //     onGalleryNavigationStart({
        //       animationDuration: eventData.scrollParams.durationInMS,
        //       continuousScroll: eventData.scrollParams.isContinuousScrolling,
        //       autoplay: !eventData.scrollParams.isManual,
        //       currentIndex: eventData.scrollParams.itemIdx,
        //       scrollDirection: eventData.scrollParams.scrollDirection,
        //       slideTransition: eventData.scrollParams.slideTransition,
        //     });
        //   ;
        //   break;
        default:
          break;
      }
    };

    const shouldShowHoverOnEdit =
      partToPreviewStateMap?.item === 'hover' ||
      partToPreviewStateMap?.image === 'hover' ||
      partToPreviewStateMap?.overlay === 'hover' ||
      partToPreviewStateMap?.info === 'hover' ||
      partToPreviewStateMap?.counter === 'hover';

    useImperativeHandle(ref, () => ({
      next: () => {
        navigationApi.next();
      },
      previous: () => {
        navigationApi?.previous();
      },
      navigateNextEnabled: () => {
        return navigationApi?.canNavigateToNext();
      },
      navigatePreviousEnabled: () => {
        return navigationApi?.canNavigateToPrevious();
      },
      navigateToIndex: (itemIdx: Number, animationDuration?: Number) => {
        navigationApi?.navigateToIndex(itemIdx, animationDuration);
      },
      currentIndex: () => {
        return navigationApi?.currentIndex();
      },
      triggerItemClick: (itemIndex: number) => {
        return navigationApi?.triggerItemClick(undefined, { itemIndex });
      },
    }));
    const customRenderers: CustomGalleryRenderers = {};
    if (shouldUseCustomImageRenderer) {
      customRenderers.imageRenderer = FastGalleryImageRenderer;
    }
    return (
      <div
        id={id}
        className={classNames(
          'root',
          containerRootClassName,
          formatClassNames(semanticClassNames.root, ...customClassNames),
          styles.root,
          shouldShowHoverOnEdit || partToPreviewStateMap?.root === 'hover'
            ? styles.hoverOnEdit
            : '',
        )}
        data-preview={partToPreviewStateMap?.root}
      >
        <Gallery
          dataList={galleryItems.map(item => {
            return {
              ...item,
              url: item.mediaUrl,
            };
          })}
          options={{ itemClickAction }}
          eventsListener={eventsListener}
          createResizedMediaUrl={getResizedMediaSrc}
          customRenderers={customRenderers}
          wrapperRef={wrapperRef}
          selectors={{
            container: classNames(
              'container-reset',
              'container',
              formatClassNames(semanticClassNames.container),
            ),
            item: classNames(
              'item-reset',
              'item',
              formatClassNames(semanticClassNames.item),
              styles.item,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
            ),
            image: classNames(
              'image',
              formatClassNames(semanticClassNames.image),
              styles.image,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
            ),
            content: classNames(
              'content',
              formatClassNames(semanticClassNames.content),
              styles.content,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
            ),
            media: classNames(
              'media',
              formatClassNames(semanticClassNames.media),
              styles.media,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
            ),
            details: classNames(
              'details',
              formatClassNames(semanticClassNames.details),
              styles.details,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
            ),
            info: classNames(
              'info',
              formatClassNames(semanticClassNames.info),
              styles.info,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
            ),
            overlay: classNames(
              'overlay',
              formatClassNames(semanticClassNames.overlay),
              styles.overlay,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
            ),
            counter: classNames(
              'counter',
              formatClassNames(semanticClassNames.counter),
              styles.counter,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
            ),
          }}
        />
        {renderChildren(
          hasResponsiveLayout ? (
            <ResponsiveContainer {...containerProps}>
              {children}
            </ResponsiveContainer>
          ) : (
            <MeshContainer {...meshProps} id={id}>
              {children}
            </MeshContainer>
          ),
        )}
      </div>
    );
  },
);

export default FastGallery;
